import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FREQUENT_CUSTOMERS_LIMIT,
  FREQUENT_CUSTOMERS_MIN_CHAR,
  SEARCH_CUSTOMERS_LIMIT,
  SEARCH_CUSTOMERS_MIN_CHAR,
} from 'appConstants';
import DynamicAutocomplete from 'components/DynamicAutocomplete';
import { useCustomers } from 'contexts/Customers';
import type { ICustomer } from 'contexts/Customers';
import { useDebouncedCallback } from 'use-debounce';
import { getCustomerLabel } from 'utils';

import GroupCustomers from './GroupCustomers';
import type { ICustomers } from './types';

export default function Customers({
  disabled,
  textFieldProps,
  selectedCustomer,
  onChange = () => {},
  onClose = () => {},
  productType,
  showFrequentCustomers,
  ...autoProps
}: ICustomers<ICustomer>) {
  const { t } = useTranslation();
  const shouldShowFrequentCustomers = Boolean(showFrequentCustomers);
  const startSearchChar = shouldShowFrequentCustomers
    ? FREQUENT_CUSTOMERS_MIN_CHAR
    : SEARCH_CUSTOMERS_MIN_CHAR;

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    onClose();
  }, [onClose]);

  const handleInputChange = useDebouncedCallback((event, value, reason) => {
    if (reason !== 'reset') setSearchTerm(value);

    if (!shouldShowFrequentCustomers) {
      if (value.length >= startSearchChar && reason === 'input') {
        setOpen(true);
      } else {
        setOpen(false);
      }
    }
  }, 500);

  const handleOnChange = useCallback(
    (event, value) => {
      onChange(value);
    },
    [onChange],
  );

  const {
    state: { loading, customers = [] },
    actions: { loadCustomers },
  } = useCustomers();

  const sortedCustomers = useMemo(() => {
    if (!shouldShowFrequentCustomers) return customers;

    return [...customers].sort((a, b) => {
      const firstIsMostUsed = Number(Boolean(a.mostUsed));
      const secondIsMostUsed = Number(Boolean(b.mostUsed));

      if (firstIsMostUsed !== secondIsMostUsed) {
        return secondIsMostUsed - firstIsMostUsed;
      }

      const firstOrderCount = a.ordersCount ?? 0;
      const secondOrderCount = b.ordersCount ?? 0;

      if (firstOrderCount !== secondOrderCount) {
        return secondOrderCount - firstOrderCount;
      }

      const firstMostRecentOrderDate = a.mostRecentOrderDate
        ? new Date(a.mostRecentOrderDate).getTime()
        : 0;
      const secondMostRecentOrderDate = b.mostRecentOrderDate
        ? new Date(b.mostRecentOrderDate).getTime()
        : 0;

      if (firstMostRecentOrderDate !== secondMostRecentOrderDate) {
        return secondMostRecentOrderDate - firstMostRecentOrderDate;
      }

      return getCustomerLabel(a).localeCompare(getCustomerLabel(b));
    });
  }, [customers, shouldShowFrequentCustomers]);

  const displayedCustomers = useMemo(() => {
    if (!shouldShowFrequentCustomers || searchTerm.trim().length > 0) {
      return sortedCustomers;
    }

    return sortedCustomers.filter((customer) => customer.mostUsed);
  }, [searchTerm, shouldShowFrequentCustomers, sortedCustomers]);

  useEffect(() => {
    if (shouldShowFrequentCustomers) {
      loadCustomers({ limit: FREQUENT_CUSTOMERS_LIMIT, productType });
    }
  }, [loadCustomers, productType, shouldShowFrequentCustomers]);

  useEffect(() => {
    if (deferredSearchTerm.length >= startSearchChar) {
      loadCustomers({ limit: SEARCH_CUSTOMERS_LIMIT, q: deferredSearchTerm, productType });
    }
  }, [deferredSearchTerm, loadCustomers, productType]);

  const getOptionLabel = (option) => {
    return getCustomerLabel(option);
  };

  return (
    <DynamicAutocomplete
      {...autoProps}
      disabled={disabled}
      textFieldProps={{
        label: t('label.accountNumber'),
        placeholder: t('placeholders.accountNumber'),
        value: searchTerm,
        name: 'accountNumber',
        ...textFieldProps,
      }}
      noOptionsText={t('label.weCouldNot')}
      open={shouldShowFrequentCustomers ? undefined : open}
      onClose={handleOnClose}
      value={selectedCustomer}
      options={displayedCustomers}
      onInputChange={handleInputChange}
      getOptionLabel={getOptionLabel}
      onChange={handleOnChange}
      loading={loading}
      displayPopupIcon={shouldShowFrequentCustomers}
      groupBy={
        shouldShowFrequentCustomers
          ? (option) => (option.mostUsed ? 'mostUsed' : 'allCustomers')
          : undefined
      }
      renderGroup={
        shouldShowFrequentCustomers
          ? (params) => (
              <GroupCustomers key={params.key} group={params.group}>
                {params.children}
              </GroupCustomers>
            )
          : undefined
      }
      isOptionEqualToValue={(option, value) =>
        option.customerReference === (typeof value === 'string' ? value : value?.customerReference)
      }
    />
  );
}
